import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "wouter";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlertTriangle,
  ArrowLeft,
  Bold,
  Eye,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function adminFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}/api/admin${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

type Kind = "notice" | "announcement";
type Mode = "all" | "group" | "specific";

interface Filters {
  plans?: string[];
  roles?: string[];
  country?: string;
  signedUpAfter?: string;
  signedUpBefore?: string;
  activeWithinDays?: number;
  inactiveForDays?: number;
  hasDocuments?: boolean;
  stripeConnected?: boolean;
  paymentFailed?: boolean;
}

interface PickedUser {
  id: number;
  name: string;
  email: string;
  plan?: string;
  role?: string;
}

interface Config {
  mode: "live" | "redirected" | "disabled";
  redirectTo: string | null;
  mailingAddress: string | null;
  adminEmail: string;
  countries: string[];
}

interface AudiencePreview {
  count: number;
  skipped: { optedOut: number; unverified: number; undeliverable: number };
  sample: { userId: number; email: string; name: string }[];
}

interface Broadcast {
  id: number;
  kind: Kind;
  subject: string;
  bodyHtml: string;
  audience: {
    mode: Mode;
    userIds?: number[];
    includeUnverified?: boolean;
    filters?: Filters;
    pickedUsers?: PickedUser[];
  };
  status: "draft" | "sending" | "sent" | "cancelled";
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  counts: Record<string, number>;
}

interface BroadcastDetail extends Broadcast {
  recipients: { id: number; email: string; name: string; status: string; error: string | null; sentAt: string | null }[];
}

const KIND_INFO: Record<Kind, { label: string; description: string }> = {
  notice: {
    label: "Service notice",
    description:
      "Policy changes, security issues, outages, billing changes: things account holders need to know. Goes to people who opted out of news, with no unsubscribe link. Don't use it for promotions.",
  },
  announcement: {
    label: "Announcement",
    description:
      "New features, offers and other news. Skips anyone who opted out, and adds an unsubscribe link and your mailing address.",
  },
};

const ACTIVITY_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "active:30", label: "Active in the last 30 days" },
  { value: "active:90", label: "Active in the last 90 days" },
  { value: "inactive:30", label: "Not active for 30+ days" },
  { value: "inactive:90", label: "Not active for 90+ days" },
  { value: "inactive:180", label: "Not active for 180+ days" },
];

const STATUS_LABELS: Record<Broadcast["status"], string> = {
  draft: "Draft",
  sending: "Sending",
  sent: "Sent",
  cancelled: "Cancelled",
};

function statusVariant(status: Broadcast["status"]): "default" | "secondary" | "destructive" | "outline" {
  if (status === "sent") return "default";
  if (status === "cancelled") return "destructive";
  if (status === "sending") return "secondary";
  return "outline";
}

function audienceSummary(b: Broadcast): string {
  if (b.audience.mode === "all") return "Everyone";
  if (b.audience.mode === "specific") {
    const n = b.audience.userIds?.length ?? 0;
    return `${n} specific ${n === 1 ? "person" : "people"}`;
  }
  return "A group";
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
}

function DeliveryBanner({ config }: { config: Config | null }) {
  if (!config || config.mode === "live") return null;
  return (
    <div className="flex gap-2 items-start rounded-md border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100 p-3 text-sm mb-4">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      {config.mode === "redirected"
        ? <span>This environment redirects email. Everything sent here, one per recipient, goes to <strong>{config.redirectTo}</strong> instead of your users.</span>
        : <span>Email sending is turned off in this environment (<code>EMAIL_ENABLED</code>), so nothing sent here reaches anyone.</span>}
    </div>
  );
}

function ToolbarButton({ onClick, active, title, children }: { onClick: () => void; active?: boolean; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center w-8 h-8 rounded ${active ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}
    >
      {children}
    </button>
  );
}

function EditorToolbar({ editor }: { editor: Editor }) {
  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address", previous ?? "https://");
    if (url === null) return;
    if (url.trim() === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url.trim() }).run();
  };
  return (
    <div className="flex flex-wrap gap-0.5 px-2 py-1.5 border-b bg-muted/40">
      <ToolbarButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-3.5 h-3.5" /></ToolbarButton>
      <ToolbarButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-3.5 h-3.5" /></ToolbarButton>
      <ToolbarButton title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-3.5 h-3.5" /></ToolbarButton>
      <ToolbarButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></ToolbarButton>
      <ToolbarButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></ToolbarButton>
      <ToolbarButton title="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon className="w-3.5 h-3.5" /></ToolbarButton>
    </div>
  );
}

function CheckOption({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
      />
      <label htmlFor={id} className="text-sm cursor-pointer select-none">{label}</label>
    </div>
  );
}

function toggled(list: string[] | undefined, value: string, on: boolean): string[] | undefined {
  const next = on ? [...(list ?? []), value] : (list ?? []).filter((v) => v !== value);
  return next.length ? next : undefined;
}

function GroupFilters({ filters, onChange, countries }: { filters: Filters; onChange: (f: Filters) => void; countries: string[] }) {
  const activity = filters.activeWithinDays
    ? `active:${filters.activeWithinDays}`
    : filters.inactiveForDays
      ? `inactive:${filters.inactiveForDays}`
      : "";
  const setActivity = (value: string) => {
    const [type, days] = value.split(":");
    onChange({
      ...filters,
      activeWithinDays: type === "active" ? Number(days) : undefined,
      inactiveForDays: type === "inactive" ? Number(days) : undefined,
    });
  };
  const selectClass = "mt-1.5 w-full h-9 rounded-md border bg-background px-2 text-sm";
  return (
    <div className="grid gap-4 sm:grid-cols-2 border rounded-md p-4">
      <div>
        <Label>Plan</Label>
        <div className="mt-2 space-y-1.5">
          {[["free", "Free"], ["trial", "On a Pro trial"], ["pro", "Pro"]].map(([value, label]) => (
            <CheckOption key={value} id={`plan-${value}`} label={label} checked={filters.plans?.includes(value) ?? false} onChange={(on) => onChange({ ...filters, plans: toggled(filters.plans, value, on) })} />
          ))}
        </div>
      </div>
      <div>
        <Label>Account type</Label>
        <div className="mt-2 space-y-1.5">
          {[["owner", "Account owners"], ["field", "Field workers"]].map(([value, label]) => (
            <CheckOption key={value} id={`role-${value}`} label={label} checked={filters.roles?.includes(value) ?? false} onChange={(on) => onChange({ ...filters, roles: toggled(filters.roles, value, on) })} />
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="filter-activity">Last active</Label>
        <select id="filter-activity" className={selectClass} value={activity} onChange={(e) => setActivity(e.target.value)}>
          {ACTIVITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="filter-country">Country</Label>
        <select id="filter-country" className={selectClass} value={filters.country ?? ""} onChange={(e) => onChange({ ...filters, country: e.target.value || undefined })}>
          <option value="">Any country</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="filter-after">Signed up on or after</Label>
        <Input id="filter-after" type="date" className="mt-1.5" value={filters.signedUpAfter ?? ""} onChange={(e) => onChange({ ...filters, signedUpAfter: e.target.value || undefined })} />
      </div>
      <div>
        <Label htmlFor="filter-before">Signed up on or before</Label>
        <Input id="filter-before" type="date" className="mt-1.5" value={filters.signedUpBefore ?? ""} onChange={(e) => onChange({ ...filters, signedUpBefore: e.target.value || undefined })} />
      </div>
      <div className="sm:col-span-2 space-y-1.5">
        <Label>Only people who</Label>
        <div className="space-y-1.5 pt-1">
          <CheckOption id="filter-docs" label="Have created a quote or invoice" checked={filters.hasDocuments ?? false} onChange={(on) => onChange({ ...filters, hasDocuments: on || undefined })} />
          <CheckOption id="filter-stripe" label="Have connected Stripe payments" checked={filters.stripeConnected ?? false} onChange={(on) => onChange({ ...filters, stripeConnected: on || undefined })} />
          <CheckOption id="filter-failed" label="Have a failed subscription payment" checked={filters.paymentFailed ?? false} onChange={(on) => onChange({ ...filters, paymentFailed: on || undefined })} />
        </div>
      </div>
    </div>
  );
}

function PeoplePicker({ picked, onChange }: { picked: PickedUser[]; onChange: (users: PickedUser[]) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PickedUser[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      adminFetch(`/broadcasts/users?q=${encodeURIComponent(query.trim())}`)
        .then((users: PickedUser[]) => { setResults(users); setError(""); })
        .catch((err: Error) => setError(err.message));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const pickedIds = new Set(picked.map((u) => u.id));
  const available = results.filter((u) => !pickedIds.has(u.id));

  return (
    <div className="border rounded-md p-4 space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input className="pl-8" placeholder="Search by name or email" aria-label="Search users" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {available.length > 0 && (
        <ul className="border rounded-md divide-y max-h-56 overflow-auto">
          {available.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                onClick={() => { onChange([...picked, user]); setQuery(""); }}
              >
                <span className="font-medium">{user.name}</span> <span className="text-muted-foreground">{user.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length >= 2 && available.length === 0 && !error && <p className="text-sm text-muted-foreground">No more matches.</p>}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {picked.map((user) => (
            <span key={user.id} className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-xs">
              {user.name} ({user.email})
              <button type="button" aria-label={`Remove ${user.name}`} onClick={() => onChange(picked.filter((u) => u.id !== user.id))}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Composer({ initial, config, onClose }: { initial: Broadcast | null; config: Config | null; onClose: (openId?: number) => void }) {
  const [id, setId] = useState<number | null>(initial?.id ?? null);
  const [kind, setKind] = useState<Kind>(initial?.kind ?? "notice");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [mode, setMode] = useState<Mode>(initial?.audience.mode ?? "all");
  const [filters, setFilters] = useState<Filters>(initial?.audience.filters ?? {});
  const [includeUnverified, setIncludeUnverified] = useState(initial?.audience.includeUnverified ?? false);
  const [picked, setPicked] = useState<PickedUser[]>(initial?.audience.pickedUsers ?? []);
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState<{ subject: string; html: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState<"" | "save" | "test" | "preview" | "send">("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const editor = useEditor({
    // The send buttons and toolbar depend on what's in the editor, so redraw as it changes.
    shouldRerenderOnTransaction: true,
    extensions: [StarterKit.configure({ link: { openOnClick: false } })],
    content: initial?.bodyHtml ?? "",
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": "Message",
        style: "min-height: 260px; outline: none; padding: 1rem; font-size: 0.9375rem; line-height: 1.7;",
      },
    },
  });

  const audience = useMemo(() => {
    if (mode === "specific") return { mode, userIds: picked.map((u) => u.id) };
    if (mode === "group") return { mode, filters, includeUnverified };
    return { mode, includeUnverified };
  }, [mode, picked, filters, includeUnverified]);

  const previewRequest = useRef(0);
  useEffect(() => {
    const request = ++previewRequest.current;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      adminFetch("/broadcasts/audience-preview", { method: "POST", body: JSON.stringify({ kind, audience }) })
        .then((data: AudiencePreview) => { if (request === previewRequest.current) setPreview(data); })
        .catch((err: Error) => { if (request === previewRequest.current) setError(err.message); })
        .finally(() => { if (request === previewRequest.current) setPreviewLoading(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [kind, audience]);

  const content = () => ({ kind, subject, bodyHtml: editor?.getHTML() ?? "" });
  const hasMessage = () => !!subject.trim() && !!editor && !editor.isEmpty;

  // Picked names are saved with the draft so they still show when it's reopened. The server only reads userIds.
  const saveDraft = async (): Promise<number> => {
    const body = JSON.stringify({ ...content(), audience: mode === "specific" ? { ...audience, pickedUsers: picked } : audience });
    if (id) {
      await adminFetch(`/broadcasts/${id}`, { method: "PUT", body });
      return id;
    }
    const created: Broadcast = await adminFetch("/broadcasts", { method: "POST", body });
    setId(created.id);
    return created.id;
  };

  const run = async (name: typeof busy, fn: () => Promise<void>) => {
    setBusy(name);
    setError("");
    setNote("");
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy("");
    }
  };

  const handleSave = () => run("save", async () => {
    await saveDraft();
    setNote("Draft saved.");
  });

  const handlePreview = () => run("preview", async () => {
    setEmailPreview(await adminFetch("/broadcasts/preview", { method: "POST", body: JSON.stringify(content()) }));
  });

  const handleTest = () => run("test", async () => {
    const result = await adminFetch("/broadcasts/test", { method: "POST", body: JSON.stringify(content()) });
    setNote(result.suppressed ? "Email sending is turned off here, so the test wasn't sent." : `Test sent to ${result.to}.`);
  });

  const handleSend = () => run("send", async () => {
    const draftId = await saveDraft();
    await adminFetch(`/broadcasts/${draftId}/send`, { method: "POST", body: JSON.stringify({ confirmCount: preview?.count ?? 0 }) });
    onClose(draftId);
  });

  const handleDelete = () => run("save", async () => {
    if (id && window.confirm("Delete this draft?")) {
      await adminFetch(`/broadcasts/${id}`, { method: "DELETE" });
      onClose();
    }
  });

  const count = preview?.count ?? 0;
  const needsAddress = kind === "announcement" && !config?.mailingAddress;
  const canReview = hasMessage() && count > 0 && !previewLoading && !needsAddress;

  return (
    <div className="space-y-6 max-w-3xl">
      <button type="button" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" onClick={() => onClose()}>
        <ArrowLeft className="w-4 h-4" /> Back to sent emails
      </button>
      <h1 className="text-[1.75rem] font-bold">{id ? "Edit draft" : "New email to users"}</h1>
      <DeliveryBanner config={config} />

      <Card>
        <CardHeader>
          <CardTitle className="text-[1.0625rem]">What kind of email is this?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(Object.keys(KIND_INFO) as Kind[]).map((value) => (
            <label key={value} className={`flex gap-3 border rounded-md p-3 cursor-pointer ${kind === value ? "border-primary bg-primary/5" : ""}`}>
              <input type="radio" name="broadcast-kind" className="mt-1 accent-primary" checked={kind === value} onChange={() => setKind(value)} />
              <span>
                <span className="block text-sm font-medium">{KIND_INFO[value].label}</span>
                <span className="block text-sm text-muted-foreground">{KIND_INFO[value].description}</span>
              </span>
            </label>
          ))}
          {needsAddress && (
            <p className="text-sm text-destructive">
              Announcements must show a mailing address. <RouterLink href="/admin/settings" className="underline">Add one in System Settings</RouterLink> before sending.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[1.0625rem]">Who gets it?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Audience">
            {([["all", "Everyone"], ["group", "A group"], ["specific", "Specific people"]] as [Mode, string][]).map(([value, label]) => (
              <Button key={value} type="button" role="radio" aria-checked={mode === value} variant={mode === value ? "default" : "outline"} size="sm" onClick={() => setMode(value)}>
                {label}
              </Button>
            ))}
          </div>

          {mode === "group" && <GroupFilters filters={filters} onChange={setFilters} countries={config?.countries ?? []} />}
          {mode === "specific" && <PeoplePicker picked={picked} onChange={setPicked} />}
          {mode !== "specific" && (
            <CheckOption id="include-unverified" label="Include people who never verified their email address" checked={includeUnverified} onChange={setIncludeUnverified} />
          )}

          <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="audience-summary" aria-live="polite">
            {previewLoading || !preview ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Counting…</span>
            ) : (
              <>
                <p><strong>{preview.count.toLocaleString()}</strong> {preview.count === 1 ? "person" : "people"} will get this.</p>
                {(preview.skipped.optedOut > 0 || preview.skipped.unverified > 0 || preview.skipped.undeliverable > 0) && (
                  <p className="text-muted-foreground mt-1">
                    Skipping{" "}
                    {[
                      preview.skipped.optedOut > 0 && `${preview.skipped.optedOut} opted out`,
                      preview.skipped.unverified > 0 && `${preview.skipped.unverified} unverified`,
                      preview.skipped.undeliverable > 0 && `${preview.skipped.undeliverable} demo or test addresses`,
                    ].filter(Boolean).join(", ")}.
                  </p>
                )}
                {preview.sample.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-muted-foreground">
                      {preview.count > preview.sample.length ? `See the first ${preview.sample.length}` : "See who"}
                    </summary>
                    <ul className="mt-2 max-h-48 overflow-auto space-y-0.5">
                      {preview.sample.map((r) => <li key={r.userId}>{r.name} <span className="text-muted-foreground">{r.email}</span></li>)}
                    </ul>
                  </details>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[1.0625rem]">Message</CardTitle>
          <CardDescription>
            Use <code>{"{{firstName}}"}</code> or <code>{"{{name}}"}</code> to fill in each person's name, in the subject or the message.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="broadcast-subject">Subject</Label>
            <Input id="broadcast-subject" className="mt-1.5" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. We're updating our Terms of Service" />
          </div>
          <div>
            <Label>Message</Label>
            <div className="mt-1.5 border rounded-md overflow-hidden broadcast-editor">
              {editor && <EditorToolbar editor={editor} />}
              <EditorContent editor={editor} />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <div className="rounded-md bg-destructive/10 text-destructive p-3 text-sm" role="alert">{error}</div>}
      {note && <div className="rounded-md bg-muted p-3 text-sm" role="status">{note}</div>}

      <div className="flex flex-wrap gap-2 pb-8">
        <Button type="button" onClick={() => { setConfirmText(""); setConfirmOpen(true); }} disabled={!canReview || !!busy}>
          <Send className="w-4 h-4 mr-2" /> Review and send
        </Button>
        <Button type="button" variant="outline" onClick={handlePreview} disabled={!!busy}>
          {busy === "preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Preview
        </Button>
        <Button type="button" variant="outline" onClick={handleTest} disabled={!hasMessage() || !!busy}>
          {busy === "test" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send me a test
        </Button>
        <Button type="button" variant="outline" onClick={handleSave} disabled={!!busy}>
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save draft
        </Button>
        {id && (
          <Button type="button" variant="ghost" onClick={handleDelete} disabled={!!busy}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete draft
          </Button>
        )}
      </div>

      <Dialog open={!!emailPreview} onOpenChange={(open) => { if (!open) setEmailPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{emailPreview?.subject || "(no subject)"}</DialogTitle>
            <DialogDescription>This is how the email looks, using your name.</DialogDescription>
          </DialogHeader>
          <iframe title="Email preview" sandbox="" srcDoc={emailPreview?.html} className="w-full h-[60vh] border rounded-md bg-white" />
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to {count.toLocaleString()} {count === 1 ? "person" : "people"}?</DialogTitle>
            <DialogDescription>Emails can't be recalled once they're delivered.</DialogDescription>
          </DialogHeader>
          <dl className="text-sm space-y-2">
            <div><dt className="text-muted-foreground">Type</dt><dd>{KIND_INFO[kind].label}</dd></div>
            <div><dt className="text-muted-foreground">Subject</dt><dd>{subject}</dd></div>
          </dl>
          <div>
            <Label htmlFor="confirm-count">Type {count} to confirm</Label>
            <Input id="confirm-count" className="mt-1.5" inputMode="numeric" autoComplete="off" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button type="button" disabled={confirmText.trim() !== String(count) || busy === "send"} onClick={() => { setConfirmOpen(false); void handleSend(); }}>
              {busy === "send" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        .broadcast-editor .tiptap p { margin-bottom: 0.875rem; }
        .broadcast-editor .tiptap h2 { font-size: 1.125rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        .broadcast-editor .tiptap ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 0.875rem; }
        .broadcast-editor .tiptap ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 0.875rem; }
        .broadcast-editor .tiptap a { text-decoration: underline; color: hsl(var(--primary)); }
      `}</style>
    </div>
  );
}

const RECIPIENT_STATUS_LABELS: Record<string, string> = {
  pending: "Waiting",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
  suppressed: "Not sent",
  skipped: "Skipped",
};

function BroadcastDetailView({ id, config, onBack }: { id: number; config: Config | null; onBack: () => void }) {
  const [detail, setDetail] = useState<BroadcastDetail | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => adminFetch(`/broadcasts/${id}`).then(setDetail).catch((err: Error) => setError(err.message)), [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (detail?.status !== "sending") return;
    const timer = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(timer);
  }, [detail?.status, load]);

  const act = async (path: string) => {
    setBusy(true);
    setError("");
    try {
      await adminFetch(`/broadcasts/${id}/${path}`, { method: "POST", body: "{}" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!detail) {
    return error ? <p className="text-destructive">{error}</p> : <Loader2 className="w-6 h-6 animate-spin" />;
  }

  const counts = detail.counts;
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const remaining = (counts.pending ?? 0) + (counts.sending ?? 0);

  return (
    <div className="space-y-6 max-w-4xl">
      <button type="button" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" onClick={onBack}>
        <ArrowLeft className="w-4 h-4" /> Back to sent emails
      </button>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[1.75rem] font-bold">{detail.subject}</h1>
          <Badge variant={statusVariant(detail.status)}>{STATUS_LABELS[detail.status]}</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {KIND_INFO[detail.kind].label} · {audienceSummary(detail)} · {formatDate(detail.startedAt)}
        </p>
      </div>
      <DeliveryBanner config={config} />

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4" data-testid="broadcast-counts">
        {[["Sent", counts.sent ?? 0], ["Waiting", remaining], ["Failed", counts.failed ?? 0], ["Skipped", (counts.skipped ?? 0) + (counts.suppressed ?? 0)]].map(([label, value]) => (
          <Card key={label}>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold">{Number(value).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{label} of {total.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">"Sent" means our email provider accepted it. It doesn't confirm it reached the inbox.</p>

      {error && <div className="rounded-md bg-destructive/10 text-destructive p-3 text-sm" role="alert">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {detail.status === "sending" && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => { if (window.confirm(`Stop now? The ${remaining} people still waiting won't get this.`)) void act("cancel"); }}>
            <X className="w-4 h-4 mr-2" /> Stop sending
          </Button>
        )}
        {detail.status !== "sending" && (counts.failed ?? 0) > 0 && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => void act("retry-failed")}>
            <RotateCcw className="w-4 h-4 mr-2" /> Retry {counts.failed} failed
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-[1.0625rem]">Recipients</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-3 font-medium">Name</th>
                <th className="py-2 pr-3 font-medium">Email</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 font-medium">Sent</th>
              </tr>
            </thead>
            <tbody>
              {detail.recipients.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{r.name}</td>
                  <td className="py-2 pr-3">{r.email}</td>
                  <td className="py-2 pr-3">
                    {RECIPIENT_STATUS_LABELS[r.status] ?? r.status}
                    {r.error && <span className="block text-xs text-muted-foreground">{r.error}</span>}
                  </td>
                  <td className="py-2 whitespace-nowrap">{formatDate(r.sentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > detail.recipients.length && <p className="text-xs text-muted-foreground mt-2">Showing the first {detail.recipients.length.toLocaleString()} of {total.toLocaleString()}.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

type View = { name: "list" } | { name: "compose"; draft: Broadcast | null } | { name: "detail"; id: number };

export default function AdminBroadcasts() {
  usePageTitle("Admin – Email Users");
  const [view, setView] = useState<View>({ name: "list" });
  const [config, setConfig] = useState<Config | null>(null);
  const [broadcasts, setBroadcasts] = useState<Broadcast[] | null>(null);
  const [error, setError] = useState("");

  const loadList = useCallback(() => {
    adminFetch("/broadcasts").then(setBroadcasts).catch((err: Error) => setError(err.message));
  }, []);

  useEffect(() => {
    adminFetch("/broadcasts/config").then(setConfig).catch((err: Error) => setError(err.message));
    loadList();
  }, [loadList]);

  if (view.name === "compose") {
    return (
      <Composer
        key={view.draft?.id ?? "new"}
        initial={view.draft}
        config={config}
        onClose={(openId) => {
          loadList();
          setView(openId ? { name: "detail", id: openId } : { name: "list" });
        }}
      />
    );
  }
  if (view.name === "detail") {
    return <BroadcastDetailView id={view.id} config={config} onBack={() => { loadList(); setView({ name: "list" }); }} />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-8">
        <div>
          <h1 className="text-[1.75rem] font-bold mb-1">Email Users</h1>
          <p className="text-muted-foreground text-[0.9375rem]">Send policy changes, notices and news to your users.</p>
        </div>
        <Button type="button" onClick={() => setView({ name: "compose", draft: null })} disabled={!!error}>
          <Plus className="w-4 h-4 mr-2" /> New email
        </Button>
      </div>
      <DeliveryBanner config={config} />
      {error && <div className="rounded-md bg-destructive/10 text-destructive p-3 text-sm mb-4" role="alert">{error}</div>}

      {!broadcasts && !error && <Loader2 className="w-6 h-6 animate-spin" />}
      {broadcasts?.length === 0 && <p className="text-muted-foreground">Nothing sent yet.</p>}
      {broadcasts && broadcasts.length > 0 && (
        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-3 font-medium">Subject</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Audience</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Sent</th>
                  <th className="py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map((b) => {
                  const total = Object.values(b.counts).reduce((sum, n) => sum + n, 0);
                  return (
                    <tr
                      key={b.id}
                      className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                      onClick={() => setView(b.status === "draft" ? { name: "compose", draft: b } : { name: "detail", id: b.id })}
                    >
                      <td className="py-2.5 pr-3 font-medium">{b.subject || "(no subject)"}</td>
                      <td className="py-2.5 pr-3">{KIND_INFO[b.kind].label}</td>
                      <td className="py-2.5 pr-3">{audienceSummary(b)}</td>
                      <td className="py-2.5 pr-3"><Badge variant={statusVariant(b.status)}>{STATUS_LABELS[b.status]}</Badge></td>
                      <td className="py-2.5 pr-3 whitespace-nowrap">{b.status === "draft" ? "" : `${(b.counts.sent ?? 0).toLocaleString()} of ${total.toLocaleString()}`}</td>
                      <td className="py-2.5 whitespace-nowrap">{formatDate(b.startedAt ?? b.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
